import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, notification } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { withLocale } from '../../l10n';
import { passwordStrengthValidation } from '../../utils/formValidation';
import LogoKiconex from '../../assets/img/png/logo.png';
import '../SignIn/SignIn.scss';
import '../../components/LoginForm/LoginForm.scss';

// Instancia especifica de axios para evitar el interceptor global en esta llamada
const resetHttp = axios.create();

function ResetPassword(props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Si llegamos aqui sin token, redirigir a forgot-password donde pueden pedir uno nuevo
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
      return;
    }

    resetHttp({
      method: 'get',
      url: import.meta.env.VITE_API_URL + 'password-reset/validate-token',
      headers: { 'Authorization': 'Basic ' + token }
    }).catch(() => {
      navigate('/login', {
        state: { errorNotification: 'Enlace no válido o expirado. Pídalo de nuevo o póngase en contacto con un administrador.' }
      });
    });
  }, [token, navigate]);

  const onSubmit = async () => {
    // 1. Validar match
    if (password !== repeatPassword) {
      notification['error']({
        message: props.lang.handle('Las contraseñas deben coincidir'),
      });
      return;
    }

    // 2. Validar fortaleza
    const strengthResult = passwordStrengthValidation(password);
    if (!strengthResult.valid) {
      notification['error']({
        message: props.lang.handle(strengthResult.message),
      });
      return;
    }

    // 3. Lanzar peticion con axios directo
    setLoading(true);
    try {
      await axios({
        method: 'put',
        url: import.meta.env.VITE_API_URL + 'password-reset/reset',
        headers: {
          'Authorization': 'Basic ' + token,
          'Content-Type': 'application/json',
          'Accept-Language': localStorage.getItem('langCode') || 'es'
        },
        data: { password }
      });

      notification['success']({
        message: props.lang.handle('Contraseña actualizada correctamente'),
        duration: 6
      });
      navigate('/login');
    } catch (err) {
      // 401: token caducado o ya usado. Mensaje especifico y redirigir a
      //      forgot-password para que el usuario pueda pedir uno nuevo.
      if (err.response && err.response.status === 401) {
        notification['error']({
          message: props.lang.handle(
            'El enlace de recuperación ha caducado o ya ha sido utilizado. Solicite uno nuevo.'
          ),
          duration: 6
        });
        navigate('/forgot-password');
        return;
      }

      // Cualquier otro 4xx con mensaje en el body (reuso de contrasena,
      // fortaleza, usuario inactivo, etc): mostrar el mensaje tal cual
      if (err.response && err.response.data && err.response.data.message) {
        notification['error']({
          message: err.response.data.message,
        });
        return;
      }

      // Error de red o servidor sin mensaje: fallback generico.
      notification['error']({
        message: props.lang.handle('Error al restablecer la contraseña'),
      });
    } finally {
      setLoading(false);
    }
  };

  // Mientras falte el token, no mostramos el formulario
  if (!token) return null;

  return (
    <div className="signInForm">
      <img className="login-logo" src={LogoKiconex} alt="Kiconex" />
      <Form className="login-form" onFinish={onSubmit}>
        <Form.Item>
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            name="password"
            placeholder={props.lang.handle('Nueva contraseña')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>

        <Form.Item>
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            name="repeatPassword"
            placeholder={props.lang.handle('Repetir contraseña')}
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="login-form-button"
          >
            {props.lang.handle('Restablecer contraseña')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export default withLocale(ResetPassword);
